import { useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Check, CheckCircle2, Loader2 } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { SignUpButton } from "@/components/auth/SignUpPrompt";
import { submitWaitlistSignup, type WaitlistSubmitOutcome } from "@/lib/waitlistRpc";
import {
  teamFieldMode,
  WAITLIST_ROLE_OPTIONS,
  waitlistSchema,
  type WaitlistFormValues,
  type WaitlistRole,
} from "@/lib/waitlistSchema";

const ERROR_COPY: Record<Exclude<WaitlistSubmitOutcome, "submitted">, string> = {
  failed: "Something went wrong on our end. Please try again.",
  invalid: "Please check your details and try again.",
  rate_limited: "Too many attempts from this network. Please try again in a little while.",
};

// No timeline is promised here that the Resend welcome emails do not make.
// Only teams and brokerages reach it: an agent is offered sign-up instead.
const SUCCESS_COPY =
  "Check your inbox for a welcome note. We'll be in touch about setting up your organization's profile.";

export type WaitlistAppearance = "default" | "marketing";

/**
 * Class names per appearance. "default" is the product's shadcn look, used by
 * the public organization pages and AuthModal's non-marketing callers;
 * "marketing" is the v3.3 marketing-site look (src/components/marketing).
 * Markup, ids, validation and the submitted payload are identical in both.
 */
const STYLES = {
  default: {
    form: "space-y-4",
    field: "space-y-1.5",
    label: "",
    roleLabel: "text-sm font-medium leading-none",
    input: "",
    hint: "text-xs text-muted-foreground",
    error: "text-sm text-destructive",
    roles: "grid grid-cols-3 gap-2",
    role: (selected: boolean) =>
      cn(
        "rounded-md border px-2 py-2 text-sm font-medium transition-colors",
        selected
          ? "border-primary bg-primary/10 text-primary"
          : "border-input bg-background text-foreground hover:bg-muted",
      ),
    alert: "text-sm text-destructive",
    success: "flex flex-col items-center gap-3 py-6 text-center",
    successTitle: "text-xl font-semibold text-foreground",
    successBody: "max-w-sm text-sm text-muted-foreground",
  },
  marketing: {
    form: "mk-card mk-form",
    field: "mk-field",
    label: "mk-field__label",
    roleLabel: "mk-field__label",
    input: "mk-input",
    hint: "mk-field__hint",
    error: "mk-field__error",
    roles: "mk-roles",
    role: () => "mk-role",
    alert: "mk-alert",
    success: "mk-card mk-success",
    successTitle: "mk-success__title",
    successBody: "mk-success__body",
  },
} as const;

const FieldError = ({ id, message, className }: { id: string; message?: string; className: string }) =>
  message ? (
    <p id={id} className={className}>
      {message}
    </p>
  ) : null;

/**
 * The waitlist form. Rendered inside WaitlistDialog and on /waitlist. The same
 * confirmation shows whether or not the email was already on the list — the
 * endpoint does not say, by design.
 */
const WaitlistForm = ({
  className,
  appearance = "default",
}: {
  className?: string;
  appearance?: WaitlistAppearance;
}) => {
  const ui = STYLES[appearance];
  const marketing = appearance === "marketing";
  // Plain elements in the marketing look: the shadcn primitives carry Tailwind
  // classes that would compete with the marketing ones on the same element.
  const Field = marketing ? "input" : Input;
  const FieldLabel = marketing ? "label" : Label;
  // /waitlist renders one form permanently and its header CTA can mount a
  // second in a dialog, so every id is prefixed per instance.
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;
  const [submitted, setSubmitted] = useState(false);
  const [errorKey, setErrorKey] = useState<keyof typeof ERROR_COPY | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: { fullName: "", email: "", brokerage: "", teamName: "", hp_ref: "" },
  });

  const role = watch("role");
  const teamMode = teamFieldMode(role);
  // The role picker is buttons, not a registered input, so React Hook Form
  // cannot focus it after an invalid submit the way it does the text fields.
  const firstRoleButton = useRef<HTMLButtonElement>(null);

  const chooseRole = (next: WaitlistRole) => {
    setValue("role", next, { shouldValidate: true, shouldDirty: true });
    // A hidden field must not carry a value the visitor can no longer see.
    if (teamFieldMode(next) === "hidden") setValue("teamName", "");
  };

  const onSubmit = async (values: WaitlistFormValues) => {
    // Agents never join the waitlist (founder decision F6): the agent choice
    // offers sign-up instead and has no submit button. Belt and braces.
    if (values.role === "agent") return;
    setErrorKey(null);
    const outcome = await submitWaitlistSignup(values);
    if (outcome === "submitted") {
      setSubmitted(true);
      return;
    }
    setErrorKey(outcome);
  };

  if (submitted) {
    return (
      <div className={cn(ui.success, className)} role="status">
        {marketing ? (
          <span className="mk-badge-circle" aria-hidden="true">
            <Check size={20} strokeWidth={2.25} />
          </span>
        ) : (
          <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden="true" />
        )}
        <h3 className={ui.successTitle}>You're on the list</h3>
        <p className={ui.successBody}>{SUCCESS_COPY}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, (invalid) => {
        // Only when role is the FIRST invalid field: RHF has already focused an
        // earlier text field otherwise, and stealing focus from it would hide
        // that error instead of this one.
        if (invalid.role && !invalid.fullName && !invalid.email) firstRoleButton.current?.focus();
      })}
      noValidate
      aria-busy={isSubmitting}
      className={cn(ui.form, className)}
    >
      <div className={ui.field}>
        <FieldLabel htmlFor={id("name")} className={ui.label}>Full name</FieldLabel>
        <Field
          id={id("name")}
          className={ui.input}
          disabled={marketing && isSubmitting}
          autoComplete="name"
          aria-invalid={!!errors.fullName}
          aria-describedby={errors.fullName ? id("name-error") : undefined}
          {...register("fullName")}
        />
        <FieldError id={id("name-error")} message={errors.fullName?.message} className={ui.error} />
      </div>

      <div className={ui.field}>
        <FieldLabel htmlFor={id("email")} className={ui.label}>Email</FieldLabel>
        <Field
          id={id("email")}
          className={ui.input}
          disabled={marketing && isSubmitting}
          type="email"
          autoComplete="email"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? id("email-error") : undefined}
          {...register("email")}
        />
        <FieldError id={id("email-error")} message={errors.email?.message} className={ui.error} />
      </div>

      <div className={ui.field}>
        <span id={id("role-label")} className={ui.roleLabel}>
          I am a…
        </span>
        <div
          role="group"
          aria-labelledby={id("role-label")}
          aria-invalid={!!errors.role}
          aria-describedby={errors.role ? id("role-error") : undefined}
          className={ui.roles}
        >
          {WAITLIST_ROLE_OPTIONS.map((option, index) => (
            <button
              key={option.value}
              ref={index === 0 ? firstRoleButton : undefined}
              type="button"
              aria-pressed={role === option.value}
              onClick={() => chooseRole(option.value)}
              disabled={marketing && isSubmitting}
              className={ui.role(role === option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <FieldError id={id("role-error")} message={errors.role?.message} className={ui.error} />
      </div>

      {role === "agent" ? (
        // Agent sign-up is open (founder decision F6, 2026-10-21): an agent is
        // sent to create an account, never onto the waitlist. Teams and
        // brokerages keep the waitlist below.
        <div className={marketing ? "mk-stack" : "space-y-3"} style={marketing ? { gap: 12 } : undefined}>
          <p className={marketing ? "mk-body" : "text-sm text-muted-foreground"}>
            Agents don't need to wait. Create a free account to explore and compare teams and brokerages now.
          </p>
          <SignUpButton
            appearance={marketing ? "marketing" : "default"}
            className={marketing ? "mk-btn mk-btn--primary mk-btn--lg mk-btn--block" : cn(buttonVariants(), "w-full")}
          >
            Create free account
          </SignUpButton>
        </div>
      ) : (
      <>
      <div className={ui.field}>
        <FieldLabel htmlFor={id("brokerage")} className={ui.label}>Brokerage</FieldLabel>
        <Field
          id={id("brokerage")}
          className={ui.input}
          disabled={marketing && isSubmitting}
          autoComplete="organization"
          aria-invalid={!!errors.brokerage}
          aria-describedby={errors.brokerage ? `${id("brokerage-help")} ${id("brokerage-error")}` : id("brokerage-help")}
          {...register("brokerage")}
        />
        <p id={id("brokerage-help")} className={ui.hint}>
          Not with one yet? Write "None".
        </p>
        <FieldError id={id("brokerage-error")} message={errors.brokerage?.message} className={ui.error} />
      </div>

      {teamMode !== "hidden" && (
        <div className={ui.field}>
          <FieldLabel htmlFor={id("team")} className={ui.label}>{teamMode === "required" ? "Team name" : "Team name (optional)"}</FieldLabel>
          <Field
            id={id("team")}
            className={ui.input}
            disabled={marketing && isSubmitting}
            aria-invalid={!!errors.teamName}
            aria-describedby={errors.teamName ? id("team-error") : undefined}
            {...register("teamName")}
          />
          <FieldError id={id("team-error")} message={errors.teamName?.message} className={ui.error} />
        </div>
      )}

      {/* Honeypot: off-screen, out of the tab order and the accessibility tree.
          The name carries no autofill signal (see waitlistSignupHandler.ts). */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <input type="text" tabIndex={-1} autoComplete="off" {...register("hp_ref")} />
      </div>

      {errorKey &&
        (marketing ? (
          <div role="alert" className={ui.alert}>
            <AlertCircle size={18} aria-hidden="true" />
            <span>{ERROR_COPY[errorKey]}</span>
          </div>
        ) : (
          <p role="alert" className={ui.alert}>
            {ERROR_COPY[errorKey]}
          </p>
        ))}

      {marketing ? (
        <button
          type="submit"
          className="mk-btn mk-btn--primary mk-btn--lg mk-btn--block"
          style={{ marginTop: 4 }}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting && <span className="mk-spinner" aria-hidden="true" />}
          Join the waitlist
        </button>
      ) : (
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          Join the waitlist
        </Button>
      )}
      </>
      )}
    </form>
  );
};

export default WaitlistForm;
